// PCM helpers: downsample float audio to 16 kHz, encode 16-bit mono WAV, base64.

/** Downsample with a box filter (averaging works as a crude low-pass; fine for speech-to-text). */
export function downsample(input: Float32Array, fromRate: number, toRate = 16000): Float32Array {
  if (fromRate === toRate) return input;
  if (fromRate < toRate) {
    // Upsample by linear interpolation (rare: some devices run the context at 8 kHz).
    const ratio = fromRate / toRate, out = new Float32Array(Math.floor(input.length / ratio));
    for (let i = 0; i < out.length; i++) {
      const x = i * ratio, j = Math.floor(x), f = x - j;
      out[i] = (input[j] || 0) * (1 - f) + (input[j + 1] || 0) * f;
    }
    return out;
  }
  const ratio = fromRate / toRate;
  const out = new Float32Array(Math.floor(input.length / ratio));
  for (let i = 0; i < out.length; i++) {
    const start = Math.round(i * ratio), end = Math.min(input.length, Math.round((i + 1) * ratio));
    let sum = 0;
    for (let j = start; j < end; j++) sum += input[j];
    out[i] = end > start ? sum / (end - start) : 0;
  }
  return out;
}

export function concat(chunks: Float32Array[]): Float32Array {
  let n = 0;
  for (const c of chunks) n += c.length;
  const out = new Float32Array(n);
  let o = 0;
  for (const c of chunks) { out.set(c, o); o += c.length; }
  return out;
}

/** 16-bit PCM mono WAV. */
export function encodeWav(samples: Float32Array, sampleRate: number): ArrayBuffer {
  const buf = new ArrayBuffer(44 + samples.length * 2);
  const v = new DataView(buf);
  const str = (o: number, s: string) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
  str(0, 'RIFF'); v.setUint32(4, 36 + samples.length * 2, true); str(8, 'WAVE');
  str(12, 'fmt '); v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, sampleRate, true); v.setUint32(28, sampleRate * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true);
  str(36, 'data'); v.setUint32(40, samples.length * 2, true);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    v.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return buf;
}

export function toBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + 0x8000)));
  return btoa(s);
}

export function rms(frame: Float32Array): number {
  let sum = 0;
  for (let i = 0; i < frame.length; i++) sum += frame[i] * frame[i];
  return Math.sqrt(sum / Math.max(1, frame.length));
}

/** A clip's loudness as `n` bars from 0 to 1 (the loudest bar = 1, square-root scaled so quieter speech still shows),
 *  from the RMS of each captured frame. Drawn in the voice bubble while the message is on its way. */
export function barsOf(frames: number[], n = 20): number[] {
  if (!frames.length) return new Array<number>(n).fill(0);
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    const a = Math.floor((i * frames.length) / n);
    const b = Math.max(a + 1, Math.floor(((i + 1) * frames.length) / n));
    let m = 0;
    for (let j = a; j < b && j < frames.length; j++) m = Math.max(m, frames[j]);
    out.push(m);
  }
  const top = Math.max(...out);
  return top > 0 ? out.map((v) => Math.round(Math.sqrt(v / top) * 100) / 100) : out;
}
